'use client'

import * as React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import { Button } from '@repo/ui/button'
import { Input } from '@repo/ui/input'
import { Label } from '@repo/ui/label'
import { CalendarCheck } from '@phosphor-icons/react'
import { type InterestValue } from '@/shared/lib/constants'
import { trpc } from '@/shared/lib/trpc'

export function CommunitySignupForm() {
  const navigate = useNavigate()

  const [formData, setFormData] = React.useState<{
    email: string
    firstName: string
    interests: InterestValue[]
    challenge: string
  }>({
    email: '',
    firstName: '',
    interests: [],
    challenge: '',
  })

  // Use TanStack Query mutation
  const mutation = useMutation({
    mutationFn: async (data: {
      email: string
      name: string
      interests?: InterestValue[]
      challenge?: string
      source: string
    }) => {
      return await trpc.newsletter.signup.mutate(data)
    },
    onSuccess: () => {
      // Navigate to thank you page on success
      navigate({ to: '/thank-you' })
    },
  })

  const handleInterestChange = (value: InterestValue, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      interests: checked
        ? [...prev.interests, value]
        : prev.interests.filter((i) => i !== value),
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    mutation.mutate({
      email: formData.email,
      name: formData.firstName,
      interests: formData.interests.length > 0 ? formData.interests : undefined,
      challenge: formData.challenge || undefined,
      source: 'workflow_kit_form',
    })
  }

  // Get error message
  const getErrorMessage = () => {
    if (!mutation.error) return ''
    const error = mutation.error as any
    if (error.data?.code === 'CONFLICT') {
      return 'This email is already subscribed'
    }
    return 'Failed to sign up. Please try again.'
  }

  const charCount = formData.challenge.length
  const maxChars = 500

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name and Email */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName" className="text-[#c3ccc7]">
            First Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="firstName"
            type="text"
            variant="form"
            placeholder="Your first name"
            value={formData.firstName}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, firstName: e.target.value }))
            }
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-[#c3ccc7]">
            Email <span className="text-red-500">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            variant="form"
            placeholder="you@example.com"
            value={formData.email}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, email: e.target.value }))
            }
            required
          />
        </div>
      </div>

      {/* Cohort waitlist - parked, not cancelled */}
      <label
        className={`
          flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all duration-200
          hover:shadow-md
          ${
            formData.interests.includes('cohort')
              ? 'border-[#80cca5] bg-[#80cca5]/10 shadow-sm'
              : 'border-[#2e3833] hover:border-[#80cca5]/50 bg-[#0e1110]'
          }
        `}
      >
        <div
          className={`mt-0.5 ${formData.interests.includes('cohort') ? 'text-[#80cca5]' : 'text-[#8b958f]'}`}
        >
          <CalendarCheck
            size={24}
            weight={formData.interests.includes('cohort') ? 'fill' : 'regular'}
          />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-white">
              Tell me when the cohort opens
            </div>
            <input
              type="checkbox"
              checked={formData.interests.includes('cohort')}
              onChange={(e) => handleInterestChange('cohort', e.target.checked)}
              className="ml-2 size-4 rounded border-[#2e3833] text-[#80cca5] focus:ring-[#80cca5]"
            />
          </div>
          <div className="text-sm text-[#8b958f] mt-1">
            A group version of the 1:1 work is planned — no date yet. Tick this
            and you'll hear first when it's real.
          </div>
        </div>
      </label>

      {/* Suggestions (Optional) */}
      <div className="space-y-2">
        <Label htmlFor="challenge" className="text-[#c3ccc7]">
          What is your biggest AI coding workflow challenge?{' '}
          <span className="text-[#8b958f]">(optional)</span>
        </Label>
        <textarea
          id="challenge"
          value={formData.challenge}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, challenge: e.target.value }))
          }
          placeholder="e.g., context gets messy, agents go off track, generated code needs too much cleanup, or I need a better review process..."
          rows={4}
          className="w-full px-4 py-3 rounded-lg border border-[#2e3833] bg-[#0e1110] text-[#f0f3f1] placeholder:text-[#8b958f] focus:ring-2 focus:ring-[#80cca5] focus:border-transparent resize-none"
          maxLength={maxChars}
        />
        <div className="flex justify-between items-center">
          <p className="text-xs text-[#8b958f]">
            Your answer shapes what I write about and what goes into OAC
          </p>
          <p
            className={`text-xs ${charCount > maxChars * 0.9 ? 'text-orange-400' : 'text-[#8b958f]'}`}
          >
            {charCount}/{maxChars}
          </p>
        </div>
      </div>

      {/* Error Message */}
      {mutation.isError && (
        <div className="p-4 rounded-lg bg-red-900/20 border border-red-800">
          <p className="text-red-400 text-sm">{getErrorMessage()}</p>
        </div>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        variant="primary-cta"
        size="cta"
        disabled={mutation.isPending}
        className="w-full"
      >
        {mutation.isPending ? 'Sending...' : 'Send Me Future Build Notes'}
      </Button>

      <p className="text-xs text-center text-[#8b958f]">
        🔒 No spam, ever. Unsubscribe anytime.
      </p>
    </form>
  )
}
